import {
  debounce,
  safeFetch,
  mediaQueries,
  trapFocus,
  reTrapFocusAfterUpdate,
  reanimateImages,
} from "../utilities/utils"
import { reinitializeSwipers } from "../utilities/swiper-element"

import "../components/tabs"
import { closeAllToggletips } from "../utilities/toggletip-engine"

class PredictiveSearch extends HTMLElement {
  constructor() {
    super()
    this.parser = new DOMParser()
    this.resultsContainers = this.querySelectorAll(".predictive-search")
    this.inputs = this.querySelectorAll(".search-input--desktop, .search-input--mobile")
    const recentSearches = sessionStorage.getItem("recentSearches")
    this.recentSearches = recentSearches ? JSON.parse(recentSearches) : []

    this.sectionId = this.dataset.sectionId || this.#computeSectionId()

    // Ensure a single persistent drawer per section in theme editor
    if (this.sectionId) {
      if (!window.__predictiveSearchRegistry) {
        window.__predictiveSearchRegistry = {}
      }

      const existingInstance = window.__predictiveSearchRegistry[this.sectionId]

      if (existingInstance && existingInstance !== this) {
        existingInstance.updateFrom(this)
        this.remove()
        return
      }

      window.__predictiveSearchRegistry[this.sectionId] = this
    }

    this.openCloseEngine = this.closest("toggletip-engine")
    this.toggletipEngines = document.querySelectorAll("toggletip-engine")
    this.drawer = this.querySelector("drawer-component")
    this.addGlobalListeners()
    this.populateRecentSearches(this)
    this.addListeners()
    this.#setupDrawerOpenListener()
    this.#setupEditorEvents()

    document.addEventListener("drawer:reinitialize", () => {
      this.reinitialize()
    })

    const predictiveSearchContainer = this.querySelector("#predictive-search")
    if (predictiveSearchContainer) {
      const resultsContainer = predictiveSearchContainer.querySelector(".predictive-search__results-container")
      if (resultsContainer) this.defaultView = resultsContainer.cloneNode(true)
    }
  }

  #computeSectionId() {
    const section = this.closest(".shopify-section")
    if (!section || !section.id) return null
    return section.id.replace("shopify-section-", "")
  }

  #getSectionId() {
    return this.sectionId || this.#computeSectionId()
  }

  updateFrom(sourceInstance) {
    const newRoot = sourceInstance.querySelector("#predictive-search")
    const currentRoot = this.querySelector("#predictive-search")

    if (!newRoot || !currentRoot) return

    // Replace only the inner content of the predictive search container
    currentRoot.innerHTML = newRoot.innerHTML

    // Re-run initialization on the existing instance so inputs, listeners,
    // and default view are all wired to the updated DOM.
    this.reinitialize()
  }

  #setupEditorEvents() {
    if (typeof Shopify === "undefined" || !Shopify.designMode) return

    document.addEventListener("shopify:section:select", (event) => {
      const sectionId = this.#getSectionId()
      if (!sectionId || event.detail?.sectionId !== sectionId) return

      window.__predictiveSearchShouldRemainOpen = true

      if (this.drawer && !this.drawer.classList.contains("open")) {
        this.drawer.open()
      }
    })

    document.addEventListener("shopify:section:deselect", (event) => {
      const sectionId = this.#getSectionId()
      if (!sectionId || event.detail?.sectionId !== sectionId) return

      window.__predictiveSearchShouldRemainOpen = false

      if (this.drawer && this.drawer.classList.contains("open")) {
        this.drawer.close()
      }
    })

    document.addEventListener("shopify:section:load", (event) => {
      const sectionId = this.#getSectionId()
      if (!sectionId || event.detail?.sectionId !== sectionId) return
      if (!window.__predictiveSearchShouldRemainOpen) return

      if (this.drawer && !this.drawer.classList.contains("open")) {
        this.drawer.open()
      }
    })
  }

  #setupDrawerOpenListener() {
    if (!this.drawer) return
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "class" && this.drawer.classList.contains("open")) {
          if (this.defaultView && this.querySelector(".predictive-search__results-container")) {
            this.setDefaultView()
          }
          break
        }
      }
    })
    observer.observe(this.drawer, { attributes: true })
  }

  connectedCallback() {
    if (this.dataset.append === "true" && document.body && this.parentElement !== document.body) {
      document.body.appendChild(this)
    }
  }

  reinitialize() {
    this.removeRecentSearchesListeners()
    this.resultsContainers = this.querySelectorAll(".predictive-search")
    this.inputs = this.querySelectorAll(".search-input--desktop, .search-input--mobile")
    this.openCloseEngine = this.closest("toggletip-engine")
    this.drawer = this.querySelector("drawer-component")
    this.addGlobalListeners()
    this.populateRecentSearches(this)
    this.addListeners()
  }

  clearRecentSearches() {
    this.recentSearches = []
    sessionStorage.removeItem("recentSearches")
    const recentSearches = this.querySelector(".recent-searches")

    if (recentSearches) {
      recentSearches.remove()
    }
  }

  removeRecentSearch(e) {
    const removeButton =
      e.target.type === "button" ? e.target : e.target.closest(".search-sidebar__remove")

    const term = removeButton.dataset.search
    if (!term) return

    const index = this.recentSearches.indexOf(term)
    if (index === -1) return

    this.recentSearches.splice(index, 1)
    sessionStorage.setItem("recentSearches", JSON.stringify(this.recentSearches))
    removeButton.closest(".recent-searches__item").remove()

    if (!this.recentSearches.length) {
      const recentSearches = this.querySelector(".recent-searches")

      if (recentSearches) {
        recentSearches.remove()
      }
    }
  }

  handleRecentSearchClick(e) {
    this.inputs.forEach((input) => {
      input.value = e.target.innerHTML
      this.toggleClearButtons(input.value)
      this.search({ target: input })
    })
  }

  addRecentSearchesListeners() {
    const buttons = this.querySelectorAll(".search-sidebar__button")
    buttons.forEach((button) => {
      button.addEventListener("click", this.handleRecentSearchClick.bind(this))
    })

    const clearButton = this.querySelector(".search-sidebar__clear")

    clearButton?.addEventListener("click", this.clearRecentSearches.bind(this))

    const removeButtons = this.querySelectorAll(".search-sidebar__remove")
    removeButtons?.forEach((removeButton) => {
      removeButton.addEventListener("click", this.removeRecentSearch.bind(this))
    })
  }

  removeRecentSearchesListeners() {
    const buttons = this.querySelectorAll(".search-sidebar__button")
    buttons.forEach((button) => {
      button.removeEventListener("click", this.handleRecentSearchClick.bind(this))
    })

    const clearButton = this.querySelector(".search-sidebar__clear")

    clearButton?.removeEventListener("click", this.clearRecentSearches.bind(this))

    const removeButtons = this.querySelectorAll(".search-sidebar__remove")
    removeButtons?.forEach((removeButton) => {
      removeButton.removeEventListener("click", this.removeRecentSearch.bind(this))
    })
  }

  addListeners() {
    this.addRecentSearchesListeners()
  }

  close() {
    this.inputs.forEach((input) => {
      input.blur()
    })

    const resultsContainers = this.querySelectorAll(".predictive-search")

    if (resultsContainers) {
      resultsContainers.forEach((container) => {
        container.classList.remove("open")
      })
    }
  }

  populateRecentSearches(section) {
    const target = section || document
    const sidebar = target.querySelector("#search-sidebar")
    const recentSearches = target.querySelector(".recent-searches")

    if (recentSearches) {
      recentSearches.remove()
    }

    if (this.recentSearches.length && sidebar) {
      const container = document.createElement("div")
      container.classList.add("search-sidebar__list", "recent-searches", "predictive-search__recent-searches")

      const title = document.createElement("span")
      title.classList.add("search-sidebar__header", "text--caption", "recent-searches__header")
      title.innerText = `${searchStrings.recent_searches}`

      const clearButton = document.createElement("button")
      clearButton.type = "button"
      clearButton.classList.add("search-sidebar__clear", "button--xs-secondary")
      clearButton.innerText = `${searchStrings.clear_all}`

      title.append(clearButton)
      container.append(title)

      const list = document.createElement("ul")

      this.recentSearches.forEach((search) => {
        const item = document.createElement("li")
        item.classList.add("search-sidebar__item", "recent-searches__item")
        const searchButton = document.createElement("button")
        searchButton.type = "button"
        searchButton.innerHTML = search
        searchButton.classList.add("search-sidebar__button")

        const removeButton = document.createElement("button")
        removeButton.type = "button"
        removeButton.classList.add("search-sidebar__remove")
        removeButton.innerHTML =
          "<span class='icon loaded' aria-hidden='true' focusable='false' role='presentation'>close</span>"
        removeButton.dataset.search = search
        item.appendChild(removeButton)
        item.appendChild(searchButton)
        list.appendChild(item)
      })

      container.appendChild(list)
      sidebar.prepend(container)
    }
  }

  globalSync(value) {
    const predictiveSearchesInputFields = document.querySelectorAll(
      "predictive-search .search-input--desktop, predictive-search .search-input--mobile",
    )

    predictiveSearchesInputFields.forEach((inputField) => {
      inputField.value = value
    })
  }

  toggleClearButtons(value) {
    const clearButtons = this.querySelectorAll("[data-predictive-search-clear]")
    const shouldShow = Boolean(value && value.trim().length)

    clearButtons.forEach((button) => {
      button.hidden = !shouldShow
    })
  }

  buildSearchResultsUrl(term) {
    const searchUrl = new URL(`${window.Shopify.routes.root}search`, window.location.origin)
    searchUrl.searchParams.set("q", term)
    searchUrl.searchParams.set("options[prefix]", "last")
    return searchUrl.toString()
  }

  toggleViewAllLinks(value) {
    const term = value?.trim() || ""
    const viewAllLinks = this.querySelectorAll("[data-predictive-search-view-all]")
    const shouldShow = Boolean(term)

    viewAllLinks.forEach((link) => {
      link.hidden = !shouldShow
      if (shouldShow) {
        link.setAttribute("href", this.buildSearchResultsUrl(term))
      }
    })
  }

  handleClearSearch(event) {
    const clearButton = event.currentTarget
    const inputWrapper = clearButton.closest(".form__input-wrapper")
    const input =
      inputWrapper?.querySelector(".search-input--desktop, .search-input--mobile") ||
      this.querySelector(".search-input--desktop, .search-input--mobile")

    if (!input) return

    input.value = ""
    this.globalSync("")
    this.toggleClearButtons("")
    this.toggleViewAllLinks("")
    this.search({ target: input })
    input.focus()
  }

  addGlobalListeners() {
    this.inputs.forEach((input) => {
      input.addEventListener(
        "input",
        debounce((e) => {
          this.globalSync(e.target.value)
          this.toggleClearButtons(e.target.value)
          this.search(e)
        }, 600),
      )
    })

    this.toggleClearButtons(this.inputs[0]?.value || "")
    this.toggleViewAllLinks("")

    const clearButtons = this.querySelectorAll("[data-predictive-search-clear]")
    clearButtons.forEach((button) => {
      button.addEventListener("click", this.handleClearSearch.bind(this))
    })

    const searchInputMobileClose = this.querySelectorAll(".predictive-search__close")
    searchInputMobileClose.forEach((close) => {
      close.addEventListener("click", () => {
        this.openCloseEngine.close()
      })
    })

    const searchTriggers = Array.from(this.querySelectorAll(".search-trigger"))
    searchTriggers.forEach((trigger) => {
      if (trigger.tagName.toLowerCase() === "input" && trigger.type === "search") {
        trigger.addEventListener("focus", () => {
          if (this.openCloseEngine && !this.isOpen) {
            closeAllToggletips()
            this.openCloseEngine.open()
            setTimeout(() => {
              const searchInput = this.querySelector(".predictive-search__header input[type=\"search\"]")
              if (searchInput) searchInput.focus()
            }, 50)
          }
        })
        trigger.addEventListener("click", (e) => {
          e.stopPropagation()
        })
      }
    })
  }

  setDefaultView() {
    if (this.defaultView) {
      const container = this.querySelector(".predictive-search__results-container")
      this.populateRecentSearches(this.defaultView)
      container.innerHTML = this.defaultView.innerHTML
      this.setFeaturedCollectionsVisibility(container, true)
      this.addRecentSearchesListeners()
      // Re-animate images consistently
      reanimateImages(container)
      reinitializeSwipers()

      // Re-establish focus trapping after DOM updates are fully processed
      const drawer = this.querySelector('drawer-component')
      reTrapFocusAfterUpdate({
        drawer,
        root: this,
        selector: '.search-input--desktop, .search-input--mobile',
        delay: 50,
      })
    }
  }

  setFeaturedCollectionsVisibility(container, isInitialState) {
    const featuredCollections = container?.querySelector(".predictive-search__featured-collections-wrapper")
    if (!featuredCollections) return
    featuredCollections.hidden = !isInitialState
  }

  async search(event) {
    const id = event.target.getAttribute("data-predictive-search")
    if (!id) return
    const resultsContainer = this.querySelector(
      '#predictive-search[data-predictive-search="' + id + '"]',
    )
    const container = resultsContainer?.querySelector(".predictive-search__results-container")
    if (!container) return
    const term = event.target.value
    const predictiveSearches = document.querySelectorAll("predictive-search")

    if (!this.defaultView) {
      const predictiveSearchContainer = this.querySelector("#predictive-search")
      if (predictiveSearchContainer) {
        this.defaultView = predictiveSearchContainer
          .querySelector(".predictive-search__results-container")
          .cloneNode(true)
      }
    }

    if (!term) {
      this.toggleViewAllLinks("")
      predictiveSearches.forEach((predictiveSearch) => predictiveSearch.setDefaultView())
      return
    }

    predictiveSearches.forEach((predictiveSearch) => {
      const predictiveContainer = predictiveSearch.querySelector(".predictive-search__results-container")
      predictiveSearch.setFeaturedCollectionsVisibility(predictiveContainer, false)
    })

    const enablePredictive = this.dataset.enablePredictiveResults !== "false"
    if (!enablePredictive) {
      this.toggleViewAllLinks("")
      predictiveSearches.forEach((predictiveSearch) => predictiveSearch.setDefaultView())
      container.classList.remove("loading")
      return
    }

    container.classList.add("loading")

    const maxResults = this.dataset.maxResults || "5"
    const limitScope = this.dataset.limitScope || "each"
    const url =
      window.Shopify.routes.root +
      `search/suggest?q=${encodeURIComponent(term)}&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor&resources[limit]=${maxResults}&resources[limit_scope]=${limitScope}&section_id=predictive-search`

    const { response, error } = await safeFetch(url)

    if (error || !response.ok) return

    const result = await response.text()

    const parsedSection = this.parser
      .parseFromString(result, "text/html")
      .querySelector("#shopify-section-predictive-search")

    this.toggleViewAllLinks(term)

    const selectors = [".search-sidebar", ".search-products"]

    predictiveSearches.forEach((predictiveSearch) => {
      if (!predictiveSearch.recentSearches.includes(term)) {
        if (predictiveSearch.recentSearches.length > 4) predictiveSearch.recentSearches.pop()
        predictiveSearch.recentSearches.unshift(term)
        sessionStorage.setItem("recentSearches", JSON.stringify(predictiveSearch.recentSearches))
        predictiveSearch.setDefaultView()
      }

      this.populateRecentSearches(parsedSection)

      selectors.forEach((selector) => {
        const element = parsedSection.querySelector(selector)

        if (!element) return

        const container = predictiveSearch.querySelector(".predictive-search__results-container")

        const existingElement = container.querySelector(selector)

        if (!existingElement) return

        existingElement.innerHTML = element.innerHTML
      })

      // Re-animate images consistently
      const containerEl = predictiveSearch.querySelector('.predictive-search__results-container')
      reanimateImages(containerEl)
      reinitializeSwipers()
      predictiveSearch.addRecentSearchesListeners()
    })

    container.classList.remove("loading")

    // Re-establish focus trapping after DOM updates are fully processed
    const drawer = this.querySelector('drawer-component')
    reTrapFocusAfterUpdate({
      drawer,
      root: this,
      selector: '.search-input--desktop, .search-input--mobile',
      delay: 50,
    })
  }

  get isOpen() {
    if (this.openCloseEngine) {
      return !!this.openCloseEngine.querySelector("[data-tooltip-trigger][open]")
    }
    return this.drawer ? this.drawer.classList.contains("open") : false
  }

  open() {
    const predictiveSearchContainer = this.querySelector("#predictive-search")
    if (predictiveSearchContainer) {
      setTimeout(() => {
        let inputToFocus;
        if (mediaQueries.isLargeDown.matches) {
          inputToFocus = predictiveSearchContainer.querySelector(".search-input--mobile");
        } else {
          inputToFocus = predictiveSearchContainer.querySelector(".search-input--desktop");
        }
        if (inputToFocus) inputToFocus.focus();
      }, 100);

      if (!this.defaultView) {
        this.defaultView = predictiveSearchContainer
          .querySelector(".predictive-search__results-container")
          .cloneNode(true)
      }
    }
  }

  disconnectedCallback() {
    if (this.isOpen) this.close()
  }
}

if (!window.customElements.get("predictive-search")) {
  window.customElements.define("predictive-search", PredictiveSearch)
}
